import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  BookOpen, CheckCircle, Clock, AlertCircle, Users, Target,
  Plus, Search, X, ChevronLeft, ChevronRight, ChevronDown,
  Edit2, Trash2, BarChart2, Settings, FileText, List,
  ToggleLeft, ToggleRight, Maximize2, Minimize2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { YearContext } from '../components/Layout/Layout';
import { useAuth }     from '../context/AuthContext';
import StatCard        from '../components/Cards/StatCard';
import ImportExport    from '../components/Common/ImportExport';
import { BarChartComponent, LineChartComponent, PieChartComponent, HorizontalBarChart } from '../components/Charts/ChartComponents';
import { toddleTrackingAPI } from '../services/api';
import { PageLoader }  from '../components/Common/LoadingSpinner';
import EmailNotificationSetup       from '../components/Common/EmailNotificationSetup';
import TelegramNotificationSetup    from '../components/Common/TelegramNotificationSetup';
import NotificationThresholdCard    from '../components/Common/NotificationThresholdCard';

// ── Status helpers ──────────────────────────────────────────────────────────
const STATUS_CFG = {
  completed: { cls: 'bg-emerald-100 text-emerald-700', label: 'Completed' },
  pending:   { cls: 'bg-amber-100  text-amber-700',   label: 'Pending'   },
  no_data:   { cls: 'bg-slate-100  text-slate-500',   label: 'No Data'   },
};
function TrkBadge({ status }) {
  const { cls, label } = STATUS_CFG[status] || STATUS_CFG.no_data;
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>{label}</span>;
}

function ProgressCell({ completed, expected }) {
  const pct   = expected > 0 ? Math.min(100, Math.round((completed / expected) * 100)) : 0;
  const color = pct >= 100 ? 'bg-emerald-500' : pct >= 60 ? 'bg-amber-400' : 'bg-red-400';
  return (
    <div className="flex items-center gap-2">
      <div className="w-16 bg-slate-100 rounded-full h-1.5">
        <div className={`h-1.5 rounded-full transition-all ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs font-mono text-slate-600 tabular-nums">{completed}/{expected}</span>
    </div>
  );
}

// ── Entry Modal (add / edit a full record) ──────────────────────────────────
const EMPTY_FORM = { teacher: '', grade: '', subject: '', week_number: 1, week_label: '', expected_periods: 4, completed_plans: 0, remarks: '' };

function EntryModal({ record, filterOptions, lpWeeks, yearId, onSave, onClose }) {
  const [form, setForm]     = useState(record ? {
    teacher: record.teacher, grade: record.grade, subject: record.subject,
    week_number: record.week_number, week_label: record.week_label || '',
    expected_periods: record.expected_periods, completed_plans: record.completed_plans,
    remarks: record.remarks || '',
  } : EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(prev => {
    const next = { ...prev, [k]: v };
    if (k === 'week_number') {
      const wk = lpWeeks.find(w => w.week_number === parseInt(v));
      if (wk) next.week_label = wk.week_label;
      else {
        const fw = filterOptions.weeks.find(w => w.number === parseInt(v));
        next.week_label = fw ? fw.label : `Week ${v}`;
      }
    }
    return next;
  });

  const computedStatus = (() => {
    const e = parseInt(form.expected_periods) || 0;
    const c = parseInt(form.completed_plans)  || 0;
    if (e === 0) return 'no_data';
    if (c >= e)  return 'completed';
    return 'pending';
  })();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.teacher.trim() || !form.grade.trim() || !form.subject.trim()) {
      toast.error('Teacher, Grade and Subject are required');
      return;
    }
    setSaving(true);
    try { await onSave({ academic_year_id: yearId, ...form }); }
    finally { setSaving(false); }
  };

  // Build week options: configured weeks first, then any from existing data
  const weekOptions = useMemo(() => {
    const seen = new Set();
    const opts = [];
    lpWeeks.filter(w => w.is_active).forEach(w => {
      seen.add(w.week_number);
      opts.push({ number: w.week_number, label: w.week_label });
    });
    filterOptions.weeks.forEach(w => {
      if (!seen.has(w.number)) opts.push({ number: w.number, label: w.label });
    });
    return opts.sort((a, b) => a.number - b.number);
  }, [lpWeeks, filterOptions.weeks]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <h3 className="text-lg font-semibold text-slate-800">{record ? 'Edit Record' : 'Add Entry'}</h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Teacher <span className="text-red-500">*</span></label>
            <input list="em-teacher-list" value={form.teacher} onChange={e => set('teacher', e.target.value)}
              placeholder="e.g. Sneha/Pinky" required
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            <datalist id="em-teacher-list">
              {filterOptions.teachers.map(t => <option key={t} value={t} />)}
            </datalist>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Grade <span className="text-red-500">*</span></label>
              <input list="em-grade-list" value={form.grade} onChange={e => set('grade', e.target.value)}
                placeholder="e.g. Grade I" required
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
              <datalist id="em-grade-list">
                {filterOptions.grades.map(g => <option key={g} value={g} />)}
              </datalist>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Subject <span className="text-red-500">*</span></label>
              <input list="em-subject-list" value={form.subject} onChange={e => set('subject', e.target.value)}
                placeholder="e.g. English" required
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
              <datalist id="em-subject-list">
                {filterOptions.subjects.map(s => <option key={s} value={s} />)}
              </datalist>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Week <span className="text-red-500">*</span></label>
              {weekOptions.length > 0 ? (
                <select value={form.week_number} onChange={e => set('week_number', e.target.value)} required
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white">
                  {weekOptions.map(w => <option key={w.number} value={w.number}>Wk {w.number}: {w.label}</option>)}
                </select>
              ) : (
                <input type="number" min="1" max="52" value={form.week_number} onChange={e => set('week_number', e.target.value)} required
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Week Label</label>
              <input value={form.week_label} onChange={e => set('week_label', e.target.value)}
                placeholder="e.g. Apr 13 – Apr 17"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Expected Periods</label>
              <input type="number" min="0" max="20" value={form.expected_periods} onChange={e => set('expected_periods', e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Completed Plans</label>
              <input type="number" min="0" max="20" value={form.completed_plans} onChange={e => set('completed_plans', e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
          </div>

          <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
            <span className="text-sm text-slate-500">Status preview:</span>
            <TrkBadge status={computedStatus} />
            <span className="text-sm text-slate-600 font-mono ml-auto">{form.completed_plans}/{form.expected_periods} plans</span>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Remarks</label>
            <textarea rows={2} value={form.remarks} onChange={e => set('remarks', e.target.value)}
              placeholder="Optional notes…"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none" />
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2 border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="flex-1 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-60">
              {saving ? 'Saving…' : record ? 'Update' : 'Add Entry'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Quick Cell Entry Modal ──────────────────────────────────────────────────
function QuickCellModal({ row, week, existingCell, expectedFromTT, yearId, teachers, onSave, onClose }) {
  const presetExpected = existingCell?.e ?? expectedFromTT ?? 0;
  const [completed, setCompleted] = useState(existingCell?.c ?? 0);
  const [expected,  setExpected]  = useState(presetExpected);
  const [teacher,   setTeacher]   = useState(row.teacher || '');
  const [saving,    setSaving]    = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!teacher.trim()) { toast.error('Teacher name is required'); return; }
    setSaving(true);
    try {
      await onSave({
        academic_year_id: yearId,
        teacher:          teacher.trim(),
        grade:            row.grade,
        subject:          row.subject,
        week_number:      week.number,
        week_label:       week.label || `Week ${week.number}`,
        expected_periods: parseInt(expected)  || 0,
        completed_plans:  parseInt(completed) || 0,
      });
    } finally { setSaving(false); }
  };

  const pct  = expected > 0 ? Math.min(100, Math.round(completed / expected * 100)) : 0;
  const bar  = pct >= 100 ? 'bg-emerald-500' : pct > 0 ? 'bg-amber-400' : 'bg-red-300';
  const needTeacher = !row.teacher;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-sm">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <div>
            <div className="text-sm font-semibold text-slate-800">{row.grade} · {row.subject}</div>
            <div className="text-xs text-slate-400 mt-0.5">{week.label || `Week ${week.number}`}</div>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600"><X size={16} /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-3">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Teacher <span className="text-red-400">*</span>
              {!needTeacher && <span className="text-slate-400 font-normal ml-1">(pre-filled)</span>}
            </label>
            <input list="qc-teacher-list" value={teacher} onChange={e => setTeacher(e.target.value)}
              placeholder="Teacher name" autoFocus={needTeacher} required
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            <datalist id="qc-teacher-list">
              {teachers.map(t => <option key={t} value={t} />)}
            </datalist>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Expected {expectedFromTT != null && <span className="text-slate-400 font-normal">(from TT)</span>}
              </label>
              <input type="number" min="0" max="20" value={expected} onChange={e => setExpected(e.target.value)}
                autoFocus={!needTeacher}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Completed</label>
              <input type="number" min="0" max="20" value={completed} onChange={e => setCompleted(e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
          </div>

          <div className="bg-slate-50 rounded-lg px-3 py-2">
            <div className="flex justify-between items-center text-xs mb-1.5">
              <span className="text-slate-500">Progress</span>
              <span className="font-mono font-semibold text-slate-700">{completed}/{expected} ({pct}%)</span>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-2">
              <div className={`h-2 rounded-full transition-all ${bar}`} style={{ width: `${pct}%` }} />
            </div>
          </div>

          <div className="flex gap-2 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 py-2 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50 transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="flex-1 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg disabled:opacity-60 transition-colors">
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── MultiSelect Dropdown ────────────────────────────────────────────────────
function MultiSelect({ label, options, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const [srch, setSrch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setSrch(''); } };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const toggle  = val => onChange(selected.includes(val) ? selected.filter(v => v !== val) : [...selected, val]);
  const visible = srch ? options.filter(o => o.toLowerCase().includes(srch.toLowerCase())) : options;

  return (
    <div ref={ref} className="relative">
      <button type="button" onClick={() => setOpen(o => !o)}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border transition-colors whitespace-nowrap ${
          selected.length > 0
            ? 'border-primary-300 bg-primary-50 text-primary-700 font-medium'
            : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
        }`}>
        <span>{label}</span>
        {selected.length > 0 && (
          <span className="bg-primary-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none min-w-[16px] text-center">
            {selected.length}
          </span>
        )}
        <ChevronDown size={13} className={`transition-transform text-slate-400 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1.5 z-50 bg-white border border-slate-200 rounded-xl shadow-xl min-w-[200px] max-w-[260px] overflow-hidden">
          {options.length > 8 && (
            <div className="px-2 pt-2 pb-1 border-b border-slate-100">
              <input autoFocus value={srch} onChange={e => setSrch(e.target.value)} placeholder="Search…"
                className="w-full px-2 py-1 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400" />
            </div>
          )}
          <div className="max-h-56 overflow-y-auto py-1">
            {selected.length > 0 && (
              <button onClick={() => { onChange([]); setSrch(''); }}
                className="w-full px-3 py-1.5 text-left text-xs text-slate-400 hover:text-red-500 hover:bg-slate-50 transition-colors border-b border-slate-100 mb-1">
                Clear all ({selected.length})
              </button>
            )}
            {visible.length === 0 && <div className="px-3 py-3 text-xs text-slate-400 text-center">No matches</div>}
            {visible.map(opt => (
              <label key={opt} className="flex items-center gap-2.5 px-3 py-1.5 hover:bg-slate-50 cursor-pointer">
                <input type="checkbox" checked={selected.includes(opt)} onChange={() => toggle(opt)}
                  className="rounded border-slate-300 text-primary-600 focus:ring-primary-500 flex-shrink-0" />
                <span className="text-sm text-slate-700 truncate" title={opt}>{opt}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Setup Tab — Week Configuration ─────────────────────────────────────────
function LpWeekSetup({ yearId, onWeeksChange }) {
  const [weeks,    setWeeks]    = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [newWeek,  setNewWeek]  = useState({ week_number: '', week_label: '', start_date: '', end_date: '' });
  const [editWeek, setEditWeek] = useState(null);
  const [saving,   setSaving]   = useState(false);

  const fmtShortDate = d => {
    if (!d) return '';
    const dt = new Date(d + 'T12:00:00');
    return `${dt.getDate()} ${['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][dt.getMonth()]}`;
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await toddleTrackingAPI.getWeeks({ academic_year_id: yearId });
      setWeeks(r.data || []);
    } catch { toast.error('Failed to load weeks'); }
    finally { setLoading(false); }
  }, [yearId]);

  useEffect(() => { load(); }, [load]);

  const nextNum = weeks.length ? Math.max(...weeks.map(w => w.week_number)) + 1 : 1;

  const addWeek = async (e) => {
    e.preventDefault();
    const num = parseInt(newWeek.week_number);
    if (!num || !newWeek.week_label.trim()) { toast.error('Week number and label are required'); return; }
    setSaving(true);
    try {
      await toddleTrackingAPI.createWeek({
        academic_year_id: yearId,
        week_number:  num,
        week_label:   newWeek.week_label.trim(),
        start_date:   newWeek.start_date || null,
        end_date:     newWeek.end_date   || null,
      });
      toast.success('Week added');
      setNewWeek({ week_number: '', week_label: '', start_date: '', end_date: '' });
      load(); onWeeksChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to add week');
    } finally { setSaving(false); }
  };

  const saveEdit = async () => {
    if (!editWeek.week_label.trim()) { toast.error('Label required'); return; }
    setSaving(true);
    try {
      await toddleTrackingAPI.updateWeek(editWeek.id, {
        week_label: editWeek.week_label.trim(),
        start_date: editWeek.start_date || null,
        end_date:   editWeek.end_date   || null,
      });
      toast.success('Week updated');
      setEditWeek(null); load(); onWeeksChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to update week');
    } finally { setSaving(false); }
  };

  const toggleActive = async (w) => {
    try {
      await toddleTrackingAPI.updateWeek(w.id, { is_active: !w.is_active });
      load(); onWeeksChange?.();
    } catch (e) { toast.error(e?.response?.data?.error || 'Failed'); }
  };

  const deleteWeek = async (w) => {
    if (!window.confirm(`Delete Week ${w.week_number}: "${w.week_label}"? This cannot be undone.`)) return;
    try {
      await toddleTrackingAPI.deleteWeek(w.id);
      toast.success('Week deleted');
      load(); onWeeksChange?.();
    } catch (e) { toast.error(e?.response?.data?.error || 'Cannot delete week'); }
  };

  return (
    <div className="space-y-5">
      <div className="glass-card p-5">
        <div className="flex items-center gap-2 mb-1">
          <Settings size={16} className="text-primary-600" />
          <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide">Week Configuration</h3>
        </div>
        <p className="text-xs text-slate-400 mb-5">
          Configure academic weeks. All matrix views and filters load weeks dynamically from this list.
          Deactivating a week hides it from the matrix but preserves existing data.
        </p>

        {/* Add week form */}
        <form onSubmit={addWeek} className="bg-slate-50 rounded-xl p-4 mb-5 border border-slate-200">
          <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-3">Add New Week</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Week # <span className="text-red-500">*</span></label>
              <input type="number" min="1" max="52"
                value={newWeek.week_number}
                onChange={e => setNewWeek(p => ({ ...p, week_number: e.target.value }))}
                placeholder={String(nextNum)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Label <span className="text-red-500">*</span></label>
              <input value={newWeek.week_label}
                onChange={e => setNewWeek(p => ({ ...p, week_label: e.target.value }))}
                placeholder="e.g. Apr 13 – Apr 17"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Start Date</label>
              <input type="date" value={newWeek.start_date}
                onChange={e => setNewWeek(p => ({ ...p, start_date: e.target.value }))}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">End Date</label>
              <input type="date" value={newWeek.end_date}
                onChange={e => setNewWeek(p => ({ ...p, end_date: e.target.value }))}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
          </div>
          <div className="flex justify-end mt-3">
            <button type="submit" disabled={saving}
              className="flex items-center gap-1.5 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg disabled:opacity-60 transition-colors">
              <Plus size={14} /> {saving ? 'Adding…' : 'Add Week'}
            </button>
          </div>
        </form>

        {/* Weeks table */}
        {loading ? (
          <div className="flex items-center justify-center py-10 text-slate-400">Loading weeks…</div>
        ) : weeks.length === 0 ? (
          <div className="text-center py-10 text-slate-400">
            <Clock size={32} className="mx-auto mb-2 opacity-30" />
            <p className="text-sm">No weeks configured. Add the first week above.</p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-slate-200">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  {['Week #','Label','Start Date','End Date','Status','Actions'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {weeks.map(w => (
                  <tr key={w.id} className={`hover:bg-slate-50 transition-colors ${!w.is_active ? 'opacity-50' : ''}`}>
                    {editWeek?.id === w.id ? (
                      <>
                        <td className="px-4 py-2 font-semibold text-slate-700 whitespace-nowrap">Wk {w.week_number}</td>
                        <td className="px-4 py-2">
                          <input value={editWeek.week_label}
                            onChange={e => setEditWeek(p => ({ ...p, week_label: e.target.value }))}
                            autoFocus
                            className="w-full border border-primary-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 min-w-[180px]" />
                        </td>
                        <td className="px-4 py-2">
                          <input type="date" value={editWeek.start_date || ''}
                            onChange={e => setEditWeek(p => ({ ...p, start_date: e.target.value }))}
                            className="border border-primary-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none" />
                        </td>
                        <td className="px-4 py-2">
                          <input type="date" value={editWeek.end_date || ''}
                            onChange={e => setEditWeek(p => ({ ...p, end_date: e.target.value }))}
                            className="border border-primary-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none" />
                        </td>
                        <td className="px-4 py-2" colSpan={2}>
                          <div className="flex items-center gap-2">
                            <button onClick={saveEdit} disabled={saving}
                              className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium rounded-lg disabled:opacity-60 transition-colors">
                              {saving ? 'Saving…' : 'Save'}
                            </button>
                            <button onClick={() => setEditWeek(null)}
                              className="px-3 py-1.5 border border-slate-200 text-xs text-slate-600 rounded-lg hover:bg-slate-50 transition-colors">
                              Cancel
                            </button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-4 py-3 font-semibold text-slate-700 whitespace-nowrap">Wk {w.week_number}</td>
                        <td className="px-4 py-3 text-slate-700">{w.week_label}</td>
                        <td className="px-4 py-3 text-slate-500 text-xs whitespace-nowrap">{w.start_date ? fmtShortDate(w.start_date.split('T')[0]) : '—'}</td>
                        <td className="px-4 py-3 text-slate-500 text-xs whitespace-nowrap">{w.end_date ? fmtShortDate(w.end_date.split('T')[0]) : '—'}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${w.is_active ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-500 border-slate-200'}`}>
                            {w.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <button onClick={() => setEditWeek({ ...w, start_date: w.start_date?.split('T')[0] || '', end_date: w.end_date?.split('T')[0] || '' })}
                              className="p-1.5 hover:bg-blue-50 text-blue-500 rounded-lg transition-colors" title="Edit">
                              <Edit2 size={13} />
                            </button>
                            <button onClick={() => toggleActive(w)}
                              className={`p-1.5 rounded-lg transition-colors ${w.is_active ? 'hover:bg-amber-50 text-amber-500' : 'hover:bg-emerald-50 text-emerald-500'}`}
                              title={w.is_active ? 'Deactivate' : 'Activate'}>
                              {w.is_active ? <ToggleRight size={13} /> : <ToggleLeft size={13} />}
                            </button>
                            <button onClick={() => deleteWeek(w)}
                              className="p-1.5 hover:bg-red-50 text-red-400 rounded-lg transition-colors" title="Delete">
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Grade sort order ────────────────────────────────────────────────────────
function gradeOrder(g) {
  const map = { nursery: 0, 'lower kg': 1, 'upper kg': 2, kg: 1, prep: 3 };
  const lower = g.toLowerCase();
  if (map[lower] !== undefined) return map[lower];
  const n = parseInt(lower.replace(/\D/g, ''));
  return isNaN(n) ? 99 : n + 10;
}
const LIMIT = 25;

// ── Matrix component (shared between read-only and editable views) ──────────
function LpMatrix({
  readOnly, sortedRows, allRows, weeks, matrixMap, ttExpectedMap,
  loading, teacherOptions, gradeOptions, subjectOptions,
  search, onSearchChange, filter, onFilterChange, sort, onSort,
  filterActive, onClearFilters, onCellClick,
}) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const handleSort = col => onSort(col);
  const sortArrow  = col => sort.col === col ? (sort.dir === 'asc' ? '↑' : '↓') : '↕';
  const sortCls    = col => sort.col === col ? 'text-amber-300' : 'text-slate-500 group-hover/sort:text-slate-300';

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') setIsFullscreen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div className={isFullscreen
      ? 'fixed inset-0 z-[9999] bg-white flex flex-col shadow-2xl overflow-hidden'
      : 'bg-white rounded-2xl shadow-card border border-slate-100 overflow-hidden'
    }>
      {/* Legend row */}
      <div className="px-6 py-3 border-b border-slate-100 flex flex-wrap items-center gap-4">
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Legend:</span>
        {[
          { cls: 'bg-emerald-100 text-emerald-800', label: 'Completed (c ≥ e)' },
          { cls: 'bg-amber-50 text-amber-700',      label: 'Partial (c < e)'   },
          { cls: 'bg-red-50 text-red-500',          label: 'Not started (0/e)' },
          { cls: 'bg-slate-100 text-slate-400',     label: 'No data'           },
        ].map(({ cls, label }) => (
          <span key={label} className="flex items-center gap-1.5 text-xs">
            <span className={`inline-block w-8 h-5 rounded text-center text-[10px] leading-5 font-semibold ${cls}`}>X/Y</span>
            {label}
          </span>
        ))}
        <span className="ml-auto flex items-center gap-3 text-xs text-slate-400">
          {loading ? 'Loading…' : (
            filterActive
              ? `${sortedRows.length} of ${allRows.length} rows`
              : `${allRows.length} rows`
          )} × {weeks.length} wks
          {readOnly && <span className="text-blue-400 font-medium">· Read Only</span>}
          <button
            onClick={() => setIsFullscreen(f => !f)}
            title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            className="p-1.5 rounded-lg border border-slate-200 text-slate-400 hover:text-primary-600 hover:border-primary-300 hover:bg-primary-50 transition-colors"
          >
            {isFullscreen ? <Minimize2 size={13}/> : <Maximize2 size={13}/>}
          </button>
        </span>
      </div>

      {/* Filter strip */}
      <div className="px-4 py-2.5 border-b border-slate-100 bg-slate-50/50 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px] max-w-[300px]">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input value={search} onChange={e => onSearchChange(e.target.value)}
            placeholder="Search teacher, grade or subject…"
            className="w-full pl-8 pr-7 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400 bg-white" />
          {search && (
            <button onClick={() => onSearchChange('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
              <X size={12} />
            </button>
          )}
        </div>
        <MultiSelect label="Teacher" options={teacherOptions} selected={filter.teachers}
          onChange={v => onFilterChange(f => ({ ...f, teachers: v }))} />
        <MultiSelect label="Grade" options={gradeOptions} selected={filter.grades}
          onChange={v => onFilterChange(f => ({ ...f, grades: v }))} />
        <MultiSelect label="Subject" options={subjectOptions} selected={filter.subjects}
          onChange={v => onFilterChange(f => ({ ...f, subjects: v }))} />
        {filterActive && (
          <button onClick={onClearFilters}
            className="flex items-center gap-1 px-2.5 py-1.5 text-xs text-slate-500 hover:text-red-500 border border-slate-200 rounded-lg bg-white hover:bg-red-50 transition-colors">
            <X size={11} /> Clear all
          </button>
        )}
      </div>

      {loading && <div className="flex items-center justify-center py-16 text-slate-400">Loading matrix…</div>}

      {!loading && allRows.length === 0 && (
        <div className="flex items-center justify-center py-16 text-slate-400">
          {readOnly ? 'No data yet.' : 'No data. Import an Excel file or add entries.'}
        </div>
      )}

      {!loading && allRows.length > 0 && sortedRows.length === 0 && (
        <div className="flex items-center justify-center py-12 text-slate-400 text-sm">No rows match the current filters.</div>
      )}

      {!loading && sortedRows.length > 0 && (
        <div className="overflow-auto" style={isFullscreen ? { flex: 1, minHeight: 0 } : { maxHeight: '70vh' }}>
          <table className="text-xs border-collapse w-max min-w-full">
            <thead className="sticky top-0 z-20">
              <tr>
                <th className="sticky left-0 z-30 bg-slate-700 text-white px-3 py-2.5 text-left border border-slate-600 min-w-[160px] whitespace-nowrap">
                  <div className="flex items-center justify-between gap-1 cursor-pointer select-none group/sort" onClick={() => handleSort('teacher')}>
                    <span className="text-xs font-semibold uppercase tracking-wide">Teacher</span>
                    <span className={`text-[11px] transition-colors ${sortCls('teacher')}`}>{sortArrow('teacher')}</span>
                  </div>
                </th>
                <th className="sticky left-[160px] z-30 bg-slate-700 text-white px-3 py-2.5 text-left border border-slate-600 min-w-[90px] whitespace-nowrap">
                  <div className="flex items-center justify-between gap-1 cursor-pointer select-none group/sort" onClick={() => handleSort('grade')}>
                    <span className="text-xs font-semibold uppercase tracking-wide">Grade</span>
                    <span className={`text-[11px] transition-colors ${sortCls('grade')}`}>{sortArrow('grade')}</span>
                  </div>
                </th>
                <th className="sticky left-[250px] z-30 bg-slate-700 text-white px-3 py-2.5 text-left border border-slate-600 min-w-[130px] whitespace-nowrap">
                  <div className="flex items-center justify-between gap-1 cursor-pointer select-none group/sort" onClick={() => handleSort('subject')}>
                    <span className="text-xs font-semibold uppercase tracking-wide">Subject</span>
                    <span className={`text-[11px] transition-colors ${sortCls('subject')}`}>{sortArrow('subject')}</span>
                  </div>
                </th>
                {weeks.map(w => (
                  <th key={w.number} className="bg-slate-600 text-white px-2 py-2 text-center font-medium border border-slate-500 min-w-[72px] whitespace-nowrap">
                    <div className="font-semibold">Wk {w.number}</div>
                    <div className="text-[10px] text-slate-300 font-normal leading-tight mt-0.5 max-w-[70px] truncate" title={w.label}>{w.label}</div>
                  </th>
                ))}
                <th className="bg-slate-700 text-white px-3 py-2.5 text-center font-semibold border border-slate-600 min-w-[70px] whitespace-nowrap">Total</th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((row, ri) => {
                const rowKey    = `${row.teacher}|||${row.grade}|||${row.subject}`;
                const cells     = matrixMap[rowKey] || {};
                const expFromTT = ttExpectedMap[`${row.grade}|||${row.subject}`];
                const isTTOnly  = !!row._fromTT;

                let totalComp = 0, totalExp = 0;
                weeks.forEach(w => {
                  const c = cells[w.number];
                  if (c) { totalComp += c.c; totalExp += c.e; }
                });
                const rowPct   = totalExp > 0 ? Math.round(totalComp / totalExp * 100) : null;
                const rowColor = rowPct === null ? 'text-slate-400'
                               : rowPct === 100  ? 'text-emerald-700 font-bold'
                               : rowPct >= 60    ? 'text-amber-700 font-semibold'
                               :                   'text-red-600 font-semibold';
                const rowBg = isTTOnly ? (ri % 2 === 0 ? 'bg-slate-50/70' : 'bg-slate-100/50')
                                       : (ri % 2 === 0 ? 'bg-white' : 'bg-slate-50');

                return (
                  <tr key={`${rowKey}-${ri}`} className={`transition-colors group ${isTTOnly ? 'opacity-60 hover:opacity-100' : 'hover:bg-sky-50'}`}>
                    <td className={`sticky left-0 z-10 ${rowBg} group-hover:bg-sky-50 px-3 py-2 border border-slate-200 font-medium whitespace-nowrap max-w-[160px] truncate`} title={row.teacher}>
                      {isTTOnly
                        ? <span className="text-slate-400 italic text-[11px]">— assign teacher —</span>
                        : <span className="text-slate-800">{row.teacher}</span>}
                    </td>
                    <td className={`sticky left-[160px] z-10 ${rowBg} group-hover:bg-sky-50 px-3 py-2 border border-slate-200 text-slate-600 whitespace-nowrap`}>
                      {row.grade}
                    </td>
                    <td className={`sticky left-[250px] z-10 ${rowBg} group-hover:bg-sky-50 px-3 py-2 border border-slate-200 text-slate-600 whitespace-nowrap max-w-[130px] truncate`} title={row.subject}>
                      {row.subject}
                      {expFromTT != null && <span className="ml-1.5 text-[10px] text-primary-400 font-normal">{expFromTT}/wk</span>}
                    </td>
                    {weeks.map(w => {
                      const cell = cells[w.number];

                      if (!cell) {
                        if (readOnly) {
                          return (
                            <td key={w.number} className="px-2 py-2 border border-slate-200 bg-slate-50 text-center text-slate-300">
                              {expFromTT != null
                                ? <span className="text-[10px] font-mono text-slate-300">—/{expFromTT}</span>
                                : <span className="text-[11px]">—</span>}
                            </td>
                          );
                        }
                        return (
                          <td key={w.number}
                            onClick={() => onCellClick?.({ row, week: w, existingCell: null, expectedFromTT: expFromTT })}
                            className="px-2 py-2 border border-slate-200 bg-slate-50 text-center text-slate-300 cursor-pointer hover:bg-sky-50 hover:text-primary-400 transition-colors"
                            title="Click to add entry">
                            {expFromTT != null
                              ? <span className="text-[10px] font-mono">?/{expFromTT}</span>
                              : <span className="text-[11px]">+</span>}
                          </td>
                        );
                      }

                      const { c, e, s } = cell;
                      const cellCls = s === 'completed' ? 'bg-emerald-100 text-emerald-800'
                                    : c === 0 && e > 0  ? 'bg-red-50 text-red-500'
                                    : s === 'pending'   ? 'bg-amber-50 text-amber-700'
                                    :                     'bg-slate-100 text-slate-400';
                      const pct = e > 0 ? Math.round(c / e * 100) : 0;

                      if (readOnly) {
                        return (
                          <td key={w.number} className={`px-2 py-2 border border-slate-200 text-center ${cellCls}`}>
                            <div className="font-semibold tabular-nums">{c}/{e}</div>
                            <div className="w-full bg-black/10 rounded-full h-1 mt-1">
                              <div className="h-1 rounded-full bg-current opacity-60" style={{ width: `${pct}%` }} />
                            </div>
                          </td>
                        );
                      }
                      return (
                        <td key={w.number}
                          onClick={() => onCellClick?.({ row, week: w, existingCell: cell, expectedFromTT: expFromTT })}
                          className={`px-2 py-2 border border-slate-200 text-center ${cellCls} cursor-pointer hover:ring-2 hover:ring-primary-300 hover:ring-inset transition-all`}
                          title="Click to edit">
                          <div className="font-semibold tabular-nums">{c}/{e}</div>
                          <div className="w-full bg-black/10 rounded-full h-1 mt-1">
                            <div className="h-1 rounded-full bg-current opacity-60" style={{ width: `${pct}%` }} />
                          </div>
                        </td>
                      );
                    })}
                    <td className={`px-3 py-2 border border-slate-200 text-center ${rowBg} group-hover:bg-sky-50`}>
                      {rowPct !== null
                        ? <div className={rowColor}>{rowPct}%</div>
                        : <span className="text-slate-300">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="sticky bottom-0 z-20">
              <tr className="bg-slate-100">
                <td colSpan={3} className="sticky left-0 z-30 bg-slate-200 px-3 py-2 font-semibold text-slate-700 border border-slate-300 text-right whitespace-nowrap">
                  Week Total
                </td>
                {weeks.map(w => {
                  let wComp = 0, wExp = 0;
                  // Use only tracked rows (not TT-only) for totals
                  Object.entries(matrixMap).forEach(([, weekCells]) => {
                    const c = weekCells[w.number];
                    if (c) { wComp += c.c; wExp += c.e; }
                  });
                  const wPct = wExp > 0 ? Math.round(wComp / wExp * 100) : null;
                  const wCls = wPct === null ? 'text-slate-400' : wPct === 100 ? 'text-emerald-700' : wPct >= 60 ? 'text-amber-700' : 'text-red-600';
                  return (
                    <td key={w.number} className="px-2 py-2 border border-slate-300 bg-slate-100 text-center">
                      {wPct !== null ? (
                        <>
                          <div className={`font-bold ${wCls}`}>{wPct}%</div>
                          <div className="text-[10px] text-slate-500 tabular-nums">{wComp}/{wExp}</div>
                        </>
                      ) : <span className="text-slate-300">—</span>}
                    </td>
                  );
                })}
                <td className="px-3 py-2 border border-slate-300 bg-slate-200 text-center">
                  {(() => {
                    let tc = 0, te = 0;
                    Object.values(matrixMap).forEach(wks => Object.values(wks).forEach(c => { tc += c.c; te += c.e; }));
                    const p = te > 0 ? Math.round(tc / te * 100) : null;
                    return p !== null ? <span className="font-bold text-slate-700">{p}%</span> : '—';
                  })()}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Main Component ──────────────────────────────────────────────────────────
export default function LessonPlan() {
  const { yearId, refreshKey } = useContext(YearContext);
  const { user, isAdmin, isSuperAdmin } = useAuth();
  const role = user?.role || '';
  const isCoordOrAbove = ['management','coordinator','admin','super_admin'].includes(role);
  const canSetup = isAdmin || isSuperAdmin;
  const canEdit  = isCoordOrAbove;

  const FULL_TABS = [
    { id: 'dashboard', label: 'Dashboard',              icon: BarChart2  },
    { id: 'plans',     label: 'Lesson Plans',           icon: BookOpen   },
    { id: 'entry',     label: 'Add Lesson Plans Status', icon: Plus      },
    ...(canSetup ? [{ id: 'setup', label: 'Setup', icon: Settings }] : []),
  ];

  const [activeTab,   setActiveTab]   = useState('dashboard');
  const [planSubView, setPlanSubView] = useState('matrix'); // 'matrix' | 'table'
  const [filters,     setFilters]     = useState({ grade: '', subject: '', teacher: '', week: '', status: '', search: '' });
  const [page,        setPage]        = useState(1);

  // Data state
  const [stats,         setStats]         = useState(null);
  const [filterOptions, setFilterOptions] = useState({ grades: [], teachers: [], subjects: [], weeks: [] });
  const [lpWeeks,       setLpWeeks]       = useState([]); // configured weeks from Setup tab
  const [list,          setList]          = useState({ data: [], total: 0, totalPages: 0 });
  const [loading,       setLoading]       = useState(true);
  const [listLoading,   setListLoading]   = useState(false);
  const [matrixRows,    setMatrixRows]    = useState([]);
  const [matrixWeeks,   setMatrixWeeks]   = useState([]);
  const [matrixMap,     setMatrixMap]     = useState({});
  const [matrixLoading, setMatrixLoading] = useState(false);
  const [ttSchedule,    setTtSchedule]    = useState([]);
  const [ttLoading,     setTtLoading]     = useState(false);
  const [localKey,      setLocalKey]      = useState(0);

  // Modal state
  const [showModal,     setShowModal]     = useState(false);
  const [editRecord,    setEditRecord]    = useState(null);
  const [quickCellData, setQuickCellData] = useState(null);

  // Editable matrix filter state
  const [matrixGlobalSearch, setMatrixGlobalSearch] = useState('');
  const [matrixFilter,       setMatrixFilter]       = useState({ teachers: [], grades: [], subjects: [] });
  const [matrixSort,         setMatrixSort]         = useState({ col: null, dir: 'asc' });

  // Read-only matrix filter state (Lesson Plans tab)
  const [roSearch,  setRoSearch]  = useState('');
  const [roFilter,  setRoFilter]  = useState({ teachers: [], grades: [], subjects: [] });
  const [roSort,    setRoSort]    = useState({ col: null, dir: 'asc' });

  const triggerRefresh = useCallback(() => setLocalKey(k => k + 1), []);

  // ── Derived state (memos) ──
  const ttExpectedMap = useMemo(() => {
    const m = {};
    ttSchedule.forEach(r => { m[`${r.grade}|||${r.subject_name}`] = r.periods_per_week; });
    return m;
  }, [ttSchedule]);

  // Merge Setup weeks (priority) + tracking-data weeks (backward compat)
  const allMatrixWeeks = useMemo(() => {
    const byNum = {};
    lpWeeks.filter(w => w.is_active).forEach(w => {
      byNum[w.week_number] = { number: w.week_number, label: w.week_label };
    });
    matrixWeeks.forEach(w => {
      if (!byNum[w.number]) byNum[w.number] = w;
    });
    return Object.values(byNum).sort((a, b) => a.number - b.number);
  }, [lpWeeks, matrixWeeks]);

  // All matrix rows: tracked + timetable-only (no tracking yet)
  const allMatrixRows = useMemo(() => {
    if (!ttSchedule.length) return matrixRows;
    const trackedPairs = new Set(matrixRows.map(r => `${r.grade}|||${r.subject}`));
    const ttOnly = ttSchedule
      .filter(r => !trackedPairs.has(`${r.grade}|||${r.subject_name}`))
      .map(r => ({ teacher: '', grade: r.grade, subject: r.subject_name, _fromTT: true }));
    return [...matrixRows, ...ttOnly].sort((a, b) => {
      const gd = gradeOrder(a.grade) - gradeOrder(b.grade);
      if (gd !== 0) return gd;
      if (a.teacher < b.teacher) return -1;
      if (a.teacher > b.teacher) return 1;
      return a.subject.localeCompare(b.subject);
    });
  }, [matrixRows, ttSchedule]);

  // Dropdown option lists
  const matrixGradeOptions   = useMemo(() => [...new Set(allMatrixRows.map(r => r.grade))].sort((a, b) => gradeOrder(a) - gradeOrder(b)), [allMatrixRows]);
  const matrixSubjectOptions = useMemo(() => [...new Set(allMatrixRows.map(r => r.subject))].sort(), [allMatrixRows]);
  const matrixTeacherOptions = useMemo(() =>
    [...new Set([...matrixRows.map(r => r.teacher), ...filterOptions.teachers])].filter(Boolean).sort(),
    [matrixRows, filterOptions.teachers]);

  // Filter + sort helper
  const applyFilterSort = useCallback((rows, srch, fil, srt) => {
    let r = rows;
    const q = srch.toLowerCase().trim();
    if (q) r = r.filter(x => x.teacher.toLowerCase().includes(q) || x.grade.toLowerCase().includes(q) || x.subject.toLowerCase().includes(q));
    if (fil.grades.length)   r = r.filter(x => fil.grades.includes(x.grade));
    if (fil.subjects.length) r = r.filter(x => fil.subjects.includes(x.subject));
    if (fil.teachers.length) r = r.filter(x => fil.teachers.includes(x.teacher));
    if (!srt.col) return r;
    const dir = srt.dir === 'asc' ? 1 : -1;
    return [...r].sort((a, b) => {
      if (srt.col === 'grade') return dir * (gradeOrder(a.grade) - gradeOrder(b.grade));
      return dir * (a[srt.col] || '').toLowerCase().localeCompare((b[srt.col] || '').toLowerCase());
    });
  }, []);

  // Editable matrix rows
  const sortedMatrixRows = useMemo(() =>
    applyFilterSort(allMatrixRows, matrixGlobalSearch, matrixFilter, matrixSort),
    [allMatrixRows, matrixGlobalSearch, matrixFilter, matrixSort, applyFilterSort]);

  // Read-only matrix rows
  const roSortedRows = useMemo(() =>
    applyFilterSort(allMatrixRows, roSearch, roFilter, roSort),
    [allMatrixRows, roSearch, roFilter, roSort, applyFilterSort]);

  const matrixFilterActive = !!(matrixGlobalSearch || matrixFilter.teachers.length || matrixFilter.grades.length || matrixFilter.subjects.length);
  const roFilterActive     = !!(roSearch || roFilter.teachers.length || roFilter.grades.length || roFilter.subjects.length);

  const handleMatrixSort = col => setMatrixSort(s => s.col === col ? { col, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' });
  const handleRoSort     = col => setRoSort(s => s.col === col ? { col, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' });
  const clearMatrixFilters = () => { setMatrixGlobalSearch(''); setMatrixFilter({ teachers: [], grades: [], subjects: [] }); };
  const clearRoFilters     = () => { setRoSearch(''); setRoFilter({ teachers: [], grades: [], subjects: [] }); };

  // ── Active filter params for API ──
  const activeFilters = useMemo(() => ({
    academic_year_id: yearId,
    ...(filters.grade   && { grade:   filters.grade   }),
    ...(filters.subject && { subject: filters.subject }),
    ...(filters.teacher && { teacher: filters.teacher }),
    ...(filters.week    && { week:    filters.week    }),
    ...(filters.status  && { status:  filters.status  }),
    ...(filters.search  && { search:  filters.search  }),
  }), [yearId, filters]);

  // ── Load stats ──
  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    toddleTrackingAPI.getStats(activeFilters)
      .then(r => { if (!ctrl.signal.aborted) setStats(r.data); })
      .catch(e => { if (!ctrl.signal.aborted) console.error('toddle stats:', e); })
      .finally(() => { if (!ctrl.signal.aborted) setLoading(false); });
    return () => ctrl.abort();
  }, [yearId, refreshKey, localKey, activeFilters]); // eslint-disable-line

  // ── Load filter options ──
  useEffect(() => {
    toddleTrackingAPI.getFilters({ academic_year_id: yearId })
      .then(r => setFilterOptions(r.data))
      .catch(() => {});
  }, [yearId, refreshKey, localKey]);

  // ── Load configured weeks from Setup ──
  useEffect(() => {
    toddleTrackingAPI.getWeeks({ academic_year_id: yearId })
      .then(r => setLpWeeks(r.data || []))
      .catch(() => {});
  }, [yearId, refreshKey, localKey]);

  // ── Auto-sync: weeks in tracking data not yet in Setup → create them ──
  useEffect(() => {
    if (!matrixWeeks.length) return;
    const lpNums = new Set(lpWeeks.map(w => w.week_number));
    const missing = matrixWeeks.filter(w => !lpNums.has(w.number));
    if (!missing.length) return;
    Promise.all(
      missing.map(w =>
        toddleTrackingAPI.createWeek({
          academic_year_id: yearId,
          week_number: w.number,
          week_label: w.label || `Week ${w.number}`,
        }).catch(() => {})
      )
    ).then(() => {
      toddleTrackingAPI.getWeeks({ academic_year_id: yearId })
        .then(r => setLpWeeks(r.data || []))
        .catch(() => {});
    });
  }, [matrixWeeks, lpWeeks, yearId]); // eslint-disable-line

  // ── Load list (for Data Table sub-view) ──
  useEffect(() => {
    if (!(activeTab === 'plans' && planSubView === 'table')) return;
    const ctrl = new AbortController();
    setListLoading(true);
    toddleTrackingAPI.getList({ ...activeFilters, page, limit: LIMIT })
      .then(r => { if (!ctrl.signal.aborted) setList(r.data); })
      .catch(() => {})
      .finally(() => { if (!ctrl.signal.aborted) setListLoading(false); });
    return () => ctrl.abort();
  }, [yearId, refreshKey, localKey, activeFilters, page, activeTab, planSubView]); // eslint-disable-line

  // ── Load matrix data ──
  useEffect(() => {
    const needMatrix = (activeTab === 'plans' && planSubView === 'matrix') || activeTab === 'entry';
    if (!needMatrix) return;
    const ctrl = new AbortController();
    setMatrixLoading(true);
    toddleTrackingAPI.getList({ ...activeFilters, limit: 1000, page: 1 })
      .then(r => {
        if (ctrl.signal.aborted) return;
        const rows = r.data.data || [];
        const rowMeta = {}, weekMeta = {}, map = {};
        for (const d of rows) {
          const key = `${d.teacher}|||${d.grade}|||${d.subject}`;
          if (!rowMeta[key]) rowMeta[key] = { teacher: d.teacher, grade: d.grade, subject: d.subject };
          if (!map[key])     map[key]     = {};
          map[key][d.week_number] = { c: d.completed_plans, e: d.expected_periods, s: d.status };
          if (!weekMeta[d.week_number]) weekMeta[d.week_number] = d.week_label || `Week ${d.week_number}`;
        }
        const sortedRows = Object.values(rowMeta).sort((a, b) => {
          const gd = gradeOrder(a.grade) - gradeOrder(b.grade);
          if (gd !== 0) return gd;
          if (a.teacher < b.teacher) return -1;
          if (a.teacher > b.teacher) return 1;
          return a.subject.localeCompare(b.subject);
        });
        const sortedWeeks = Object.entries(weekMeta)
          .sort((a, b) => parseInt(a[0]) - parseInt(b[0]))
          .map(([num, label]) => ({ number: parseInt(num), label }));
        setMatrixRows(sortedRows);
        setMatrixWeeks(sortedWeeks);
        setMatrixMap(map);
      })
      .catch(e => { if (!ctrl.signal.aborted) console.error('matrix load:', e); })
      .finally(() => { if (!ctrl.signal.aborted) setMatrixLoading(false); });
    return () => ctrl.abort();
  }, [yearId, refreshKey, localKey, activeFilters, activeTab, planSubView]); // eslint-disable-line

  // ── Load timetable schedule ──
  useEffect(() => {
    const needMatrix = (activeTab === 'plans' && planSubView === 'matrix') || activeTab === 'entry';
    if (!needMatrix) return;
    setTtLoading(true);
    toddleTrackingAPI.getSchedule({ academic_year_id: yearId })
      .then(r => setTtSchedule(r.data))
      .catch(() => {})
      .finally(() => setTtLoading(false));
  }, [yearId, activeTab, planSubView]);

  const handleFilterChange = (k, v) => { setFilters(prev => ({ ...prev, [k]: v })); setPage(1); };

  const handleKpiClick = (target) => {
    if (target === 'dashboard') {
      setActiveTab('dashboard');
    } else if (target === 'matrix') {
      setActiveTab('plans');
      setPlanSubView('matrix');
    } else {
      setActiveTab('plans');
      setPlanSubView('table');
      setFilters(prev => ({ ...prev, status: target }));
      setPage(1);
    }
  };

  const handleSave = async (formData) => {
    try {
      if (editRecord) {
        await toddleTrackingAPI.update(editRecord.id, formData);
        toast.success('Record updated');
      } else {
        await toddleTrackingAPI.create(formData);
        toast.success('Entry added');
      }
      setShowModal(false);
      setEditRecord(null);
      triggerRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Save failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this record? This cannot be undone.')) return;
    try {
      await toddleTrackingAPI.remove(id);
      toast.success('Deleted');
      triggerRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Delete failed');
    }
  };

  const handleQuickSave = useCallback(async (formData) => {
    try {
      await toddleTrackingAPI.create(formData);
      toast.success('Saved');
      setQuickCellData(null);
      triggerRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Save failed');
      throw e;
    }
  }, [triggerRefresh]);

  // Chart data
  const overall     = stats?.overall || {};
  const gradeData   = (stats?.byGrade     || []).map(g => ({ name: g.grade, pct: Number(g.pct) || 0 }));
  const teacherData = (stats?.byTeacher   || []).slice(0,15).map(t => ({ name: t.teacher.split('/')[0].trim(), pct: Number(t.pct) || 0 }));
  const weekData    = (stats?.weeklyTrend || []).map(w => ({ week: `W${w.week_number}`, pct: Number(w.pct) || 0, expected: Number(w.expected) || 0, completed: Number(w.completed) || 0 }));
  const subjectData = (stats?.bySubject   || []).map(s => ({ name: s.subject, pct: Number(s.pct) || 0 }));

  if (loading && !stats) return <PageLoader />;

  // ── Render ──
  return (
    <div className="space-y-5 animate-fade-in">

      {/* ── Page header with tab navigation ── */}
      <div className="space-y-2.5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h2 className="text-2xl font-display font-bold text-slate-800">Toddle Lesson Plan Tracking</h2>
            <p className="text-slate-500 text-sm mt-0.5">Week-by-week lesson plan completion across all grades and subjects</p>
          </div>

          {/* Tab switcher */}
          <div className="flex items-center border border-slate-200 rounded-xl overflow-hidden bg-white shadow-sm shrink-0">
            {FULL_TABS.map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium transition-colors ${
                  activeTab === tab.id ? 'bg-primary-600 text-white' : 'text-slate-600 hover:bg-slate-50'
                }`}>
                <tab.icon size={13} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Second row — Import/Export toolbar (entry tab only) */}
        {activeTab === 'entry' && canEdit && (
          <div className="flex items-center gap-2 flex-wrap">
            <ImportExport
              module="toddleLPTracking"
              onImport={fd => toddleTrackingAPI.import(fd, { academic_year_id: yearId })}
              onExport={() => toddleTrackingAPI.export(activeFilters)}
              onSampleTemplate={toddleTrackingAPI.sampleTemplate}
              onSuccess={triggerRefresh}
            />
          </div>
        )}
        {/* Second row — Export toolbar (plans tab only) */}
        {activeTab === 'plans' && (
          <div className="flex items-center gap-2">
            <button onClick={() => toddleTrackingAPI.export(activeFilters).then(r => {
              const url = URL.createObjectURL(r.data);
              const a = document.createElement('a'); a.href = url; a.download = 'lesson_plan_status.xlsx'; a.click();
              URL.revokeObjectURL(url);
            }).catch(() => toast.error('Export failed'))}
              className="flex items-center gap-1.5 px-3 py-2 border border-slate-200 bg-white text-sm text-slate-600 rounded-xl hover:bg-slate-50 transition-colors">
              <FileText size={14} /> Export
            </button>
          </div>
        )}
      </div>

      {/* ── Stat cards (Dashboard tab only) ── */}
      {activeTab === 'dashboard' && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          <StatCard title="Completion"    value={`${overall.completion_pct  || 0}%`} icon={Target}      color="green"  onClick={() => handleKpiClick('dashboard')} />
          <StatCard title="Teachers"      value={overall.total_teachers || 0}         icon={Users}       color="blue"   onClick={() => handleKpiClick('matrix')} />
          <StatCard title="Total Records" value={overall.total_records  || 0}         icon={BookOpen}    color="indigo" onClick={() => handleKpiClick('')} />
          <StatCard title="Completed"     value={overall.completed_records || 0}      icon={CheckCircle} color="teal"   onClick={() => handleKpiClick('completed')} />
          <StatCard title="Pending"       value={overall.pending_records   || 0}      icon={Clock}       color="yellow" onClick={() => handleKpiClick('pending')} />
          <StatCard title="No Data"       value={overall.no_data_records   || 0}      icon={AlertCircle} color="red"    onClick={() => handleKpiClick('no_data')} />
        </div>
      )}

      {/* ── Global filter bar (hidden on Dashboard and Setup) ── */}
      {activeTab !== 'dashboard' && activeTab !== 'setup' && (
        <div className="bg-white rounded-xl p-4 shadow-card border border-slate-100">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <select value={filters.grade} onChange={e => handleFilterChange('grade', e.target.value)}
              className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white text-slate-700">
              <option value="">All Grades</option>
              {filterOptions.grades.map(g => <option key={g} value={g}>{g}</option>)}
            </select>

            <select value={filters.subject} onChange={e => handleFilterChange('subject', e.target.value)}
              className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white text-slate-700">
              <option value="">All Subjects</option>
              {filterOptions.subjects.map(s => <option key={s} value={s}>{s}</option>)}
            </select>

            <select value={filters.teacher} onChange={e => handleFilterChange('teacher', e.target.value)}
              className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white text-slate-700">
              <option value="">All Teachers</option>
              {filterOptions.teachers.map(t => <option key={t} value={t}>{t}</option>)}
            </select>

            <select value={filters.week} onChange={e => handleFilterChange('week', e.target.value)}
              className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white text-slate-700">
              <option value="">All Weeks</option>
              {/* Prefer configured weeks, fall back to filter options */}
              {(lpWeeks.length > 0 ? lpWeeks.map(w => ({ number: w.week_number, label: w.week_label })) : filterOptions.weeks).map(w => (
                <option key={w.number} value={w.number}>Wk {w.number}: {w.label}</option>
              ))}
            </select>

            <select value={filters.status} onChange={e => handleFilterChange('status', e.target.value)}
              className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white text-slate-700">
              <option value="">All Statuses</option>
              <option value="completed">Completed</option>
              <option value="pending">Pending</option>
              <option value="no_data">No Data</option>
            </select>

            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <input type="text" placeholder="Search…" value={filters.search}
                onChange={e => handleFilterChange('search', e.target.value)}
                className="w-full pl-8 pr-8 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
              {filters.search && (
                <button onClick={() => handleFilterChange('search', '')} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  <X size={14} />
                </button>
              )}
            </div>
          </div>

          {Object.entries(filters).some(([, v]) => v) && (
            <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-slate-100">
              {Object.entries(filters).filter(([, v]) => v).map(([k, v]) => (
                <span key={k} className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary-50 text-primary-700 rounded-full text-xs font-medium">
                  {k}: {v}
                  <button onClick={() => handleFilterChange(k, '')} className="hover:text-primary-900"><X size={10} /></button>
                </span>
              ))}
              <button onClick={() => { setFilters({ grade: '', subject: '', teacher: '', week: '', status: '', search: '' }); setPage(1); }}
                className="text-xs text-slate-400 hover:text-slate-600 underline">
                Clear all
              </button>
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          TAB: Dashboard
      ══════════════════════════════════════════════════════════════ */}
      {activeTab === 'dashboard' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <PieChartComponent title="Status Distribution" data={stats?.statusDistribution || []} donut />
            <HorizontalBarChart title="Completion % by Grade" data={gradeData} dataKey="pct" labelKey="name" color="#10b981" />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <LineChartComponent
              title="Weekly Trend — Completion %"
              data={weekData} xKey="week"
              lines={[{ key: 'pct', label: 'Completion %', color: '#0ea5e9' }]} />
            <BarChartComponent
              title="Weekly: Expected vs Completed"
              data={weekData} xKey="week"
              bars={[
                { key: 'expected',  label: 'Expected',  color: '#94a3b8' },
                { key: 'completed', label: 'Completed', color: '#10b981' },
              ]} />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <HorizontalBarChart title="Completion % by Teacher (Top 15)" data={teacherData} dataKey="pct" labelKey="name" color="#8b5cf6" />
            <HorizontalBarChart title="Completion % by Subject"          data={subjectData} dataKey="pct" labelKey="name" color="#f59e0b" />
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          TAB: Lesson Plans (Read-Only)
      ══════════════════════════════════════════════════════════════ */}
      {activeTab === 'plans' && (
        <div className="space-y-4">
          {/* Sub-view switcher */}
          <div className="flex gap-2">
            <button onClick={() => setPlanSubView('matrix')}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-xl border transition-colors ${
                planSubView === 'matrix' ? 'bg-primary-600 text-white border-primary-600 shadow-sm' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
              }`}>
              <BarChart2 size={14} /> Matrix View
            </button>
            <button onClick={() => setPlanSubView('table')}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-xl border transition-colors ${
                planSubView === 'table' ? 'bg-primary-600 text-white border-primary-600 shadow-sm' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
              }`}>
              <List size={14} /> Data Table
            </button>
          </div>

          {/* Read-only matrix */}
          {planSubView === 'matrix' && (
            <LpMatrix
              readOnly
              sortedRows={roSortedRows}
              allRows={allMatrixRows}
              weeks={allMatrixWeeks}
              matrixMap={matrixMap}
              ttExpectedMap={ttExpectedMap}
              loading={matrixLoading || ttLoading}
              teacherOptions={matrixTeacherOptions}
              gradeOptions={matrixGradeOptions}
              subjectOptions={matrixSubjectOptions}
              search={roSearch}
              onSearchChange={setRoSearch}
              filter={roFilter}
              onFilterChange={setRoFilter}
              sort={roSort}
              onSort={handleRoSort}
              filterActive={roFilterActive}
              onClearFilters={clearRoFilters}
            />
          )}

          {/* Read-only data table */}
          {planSubView === 'table' && (
            <div className="bg-white rounded-2xl shadow-card border border-slate-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                <span className="text-sm text-slate-500">
                  {listLoading ? 'Loading…' : `${list.total.toLocaleString()} records`}
                </span>
                <span className="text-xs text-slate-400">Page {page} of {list.totalPages || 1}</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-100">
                      {['Teacher','Grade','Subject','Week','Expected','Completed','Progress','Status','Remarks','Updated'].map(h => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {listLoading && <tr><td colSpan={10} className="px-4 py-8 text-center text-slate-400">Loading…</td></tr>}
                    {!listLoading && list.data.length === 0 && (
                      <tr><td colSpan={10} className="px-4 py-12 text-center text-slate-400">No records found.</td></tr>
                    )}
                    {!listLoading && list.data.map(row => (
                      <tr key={row.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-4 py-3 font-medium text-slate-800 max-w-[160px] truncate" title={row.teacher}>{row.teacher}</td>
                        <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{row.grade}</td>
                        <td className="px-4 py-3 text-slate-600 max-w-[140px] truncate" title={row.subject}>{row.subject}</td>
                        <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                          <div className="font-medium">Wk {row.week_number}</div>
                          {row.week_label && <div className="text-xs text-slate-400 mt-0.5">{row.week_label}</div>}
                        </td>
                        <td className="px-4 py-3 text-center text-slate-600">{row.expected_periods}</td>
                        <td className="px-4 py-3 text-center text-slate-600">{row.completed_plans}</td>
                        <td className="px-4 py-3"><ProgressCell completed={row.completed_plans} expected={row.expected_periods} /></td>
                        <td className="px-4 py-3"><TrkBadge status={row.status} /></td>
                        <td className="px-4 py-3 text-slate-500 max-w-[160px] truncate text-xs" title={row.remarks || ''}>{row.remarks || '—'}</td>
                        <td className="px-4 py-3 text-slate-400 text-xs whitespace-nowrap">
                          {row.updated_at ? new Date(row.updated_at).toLocaleDateString('en-IN', { day:'2-digit', month:'short' }) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {list.totalPages > 1 && (
                <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between">
                  <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed">
                    <ChevronLeft size={16} /> Prev
                  </button>
                  <div className="flex gap-1">
                    {Array.from({ length: Math.min(7, list.totalPages) }, (_, i) => {
                      let pg;
                      if (list.totalPages <= 7) pg = i + 1;
                      else if (page <= 4) pg = i < 6 ? i + 1 : list.totalPages;
                      else if (page >= list.totalPages - 3) pg = i === 0 ? 1 : list.totalPages - 6 + i;
                      else { const m = [1, page-2, page-1, page, page+1, page+2, list.totalPages]; pg = m[i]; }
                      return (
                        <button key={i} onClick={() => setPage(pg)}
                          className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${pg === page ? 'bg-primary-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
                          {pg}
                        </button>
                      );
                    })}
                  </div>
                  <button onClick={() => setPage(p => Math.min(list.totalPages, p + 1))} disabled={page >= list.totalPages}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed">
                    Next <ChevronRight size={16} />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          TAB: Add Lesson Plans Status (Editable)
      ══════════════════════════════════════════════════════════════ */}
      {activeTab === 'entry' && (
        <div className="space-y-4">
          {!canEdit ? (
            <div className="glass-card p-8 text-center text-slate-400">
              <AlertCircle size={32} className="mx-auto mb-2 opacity-30" />
              <p className="text-sm font-medium">You don't have permission to add or edit lesson plan status.</p>
            </div>
          ) : (
            <LpMatrix
              readOnly={false}
              sortedRows={sortedMatrixRows}
              allRows={allMatrixRows}
              weeks={allMatrixWeeks}
              matrixMap={matrixMap}
              ttExpectedMap={ttExpectedMap}
              loading={matrixLoading || ttLoading}
              teacherOptions={matrixTeacherOptions}
              gradeOptions={matrixGradeOptions}
              subjectOptions={matrixSubjectOptions}
              search={matrixGlobalSearch}
              onSearchChange={setMatrixGlobalSearch}
              filter={matrixFilter}
              onFilterChange={setMatrixFilter}
              sort={matrixSort}
              onSort={handleMatrixSort}
              filterActive={matrixFilterActive}
              onClearFilters={clearMatrixFilters}
              onCellClick={setQuickCellData}
            />
          )}

        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          TAB: Setup (admin only)
      ══════════════════════════════════════════════════════════════ */}
      {activeTab === 'setup' && canSetup && (
        <div className="space-y-5">
          <LpWeekSetup yearId={yearId} onWeeksChange={triggerRefresh} />
          <NotificationThresholdCard
            module="toddleLessonPlan"
            yearId={yearId}
            title="Lesson Plan Completion Thresholds"
            thresholds={[
              {
                key: 'weekly_completion_target_pct',
                label: 'Weekly completion target (%)',
                description: 'Send a notification if the weekly lesson plan completion percentage falls below this target.',
                defaultValue: 80,
                min: 0,
                max: 100,
                suffix: '% completion / week',
              },
            ]}
          />
          <EmailNotificationSetup    module="toddleLessonPlan" moduleLabel="Toddle Lesson Plan" yearId={yearId} />
          <TelegramNotificationSetup module="toddleLessonPlan" moduleLabel="Toddle Lesson Plan" yearId={yearId} />
        </div>
      )}
      {activeTab === 'setup' && !canSetup && (
        <div className="glass-card p-8 text-center text-slate-400">
          <Settings size={32} className="mx-auto mb-2 opacity-30" />
          <p className="text-sm font-medium">Setup is restricted to administrators.</p>
        </div>
      )}

      {/* ── Modals ── */}
      {showModal && (
        <EntryModal
          record={editRecord}
          filterOptions={filterOptions}
          lpWeeks={lpWeeks}
          yearId={yearId}
          onSave={handleSave}
          onClose={() => { setShowModal(false); setEditRecord(null); }}
        />
      )}

      {quickCellData && (
        <QuickCellModal
          row={quickCellData.row}
          week={quickCellData.week}
          existingCell={quickCellData.existingCell}
          expectedFromTT={quickCellData.expectedFromTT}
          yearId={yearId}
          teachers={filterOptions.teachers}
          onSave={handleQuickSave}
          onClose={() => setQuickCellData(null)}
        />
      )}
    </div>
  );
}
